<script setup lang="ts">
import { get } from '@/api'

definePage({
  meta: {
    action: 'manage',
    subject: 'Admin',
  },
})

const { snackbar } = useErrorHandler()

const logs = ref<any[]>([])
const loading = ref(false)

const pagination = ref({
  page: 1,
  perPage: 20,
  total: 0,
})

const filters = ref({
  type: '',
  status: '',
  search: '',
})

interface NotificationLog {
  id: number
  type: string
  recipient: string | null
  subject: string | null
  content: string | null
  status: string
  error_message: string | null
  created_at: string
  template?: {
    id: number
    name: string
  }
}

const fetchLogs = async () => {
  loading.value = true
  try {
    const params = new URLSearchParams()
    if (filters.value.type)
      params.append('type', filters.value.type)
    if (filters.value.status)
      params.append('status', filters.value.status)
    if (filters.value.search)
      params.append('search', filters.value.search)
    params.append('page', pagination.value.page.toString())
    params.append('per_page', pagination.value.perPage.toString())

    const { success, data } = await get<{ data: NotificationLog[]; current_page: number; last_page: number; per_page: number; total: number }>(
      `/regle/notification-logs?${params.toString()}`,
    )

    if (success && data) {
      logs.value = data.data
      pagination.value.total = data.total
      pagination.value.page = data.current_page
      pagination.value.perPage = data.per_page
    }
  }
  catch {
    snackbar.error('알림 이력을 불러오는데 실패했습니다.')
  }
  finally {
    loading.value = false
  }
}

const getTypeIcon = (type: string): string => {
  const icons: Record<string, string> = {
    email: 'tabler-mail',
    slack: 'tabler-brand-slack',
    notion: 'tabler-brand-notion',
    sms: 'tabler-message',
  }

  return icons[type] || 'tabler-bell'
}

const getTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    email: 'primary',
    slack: 'info',
    notion: 'info',
    sms: 'warning',
  }

  return colors[type] || 'grey'
}

const getStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    sent: 'success',
    failed: 'error',
    pending: 'warning',
  }

  return colors[status] || 'grey'
}

const formatDate = (date: string): string => {
  return new Date(date).toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const refreshPage = () => {
  pagination.value.page = 1
  fetchLogs()
}

onMounted(fetchLogs)
</script>

<template>
  <VRow>
    <VCol cols="12">
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between">
          <span>알림 이력</span>
        </VCardTitle>

        <VCardText>
          <!-- 필터 -->
          <VRow class="mb-4">
            <VCol
              cols="12"
              sm="4"
            >
              <VSelect
                v-model="filters.type"
                :items="[
                  { title: '전체', value: '' },
                  { title: '이메일', value: 'email' },
                  { title: 'Slack', value: 'slack' },
                  { title: 'Notion', value: 'notion' },
                  { title: 'SMS', value: 'sms' },
                ]"
                label="유형"
                hide-details
                density="compact"
                @update:model-value="refreshPage"
              />
            </VCol>

            <VCol
              cols="12"
              sm="4"
            >
              <VSelect
                v-model="filters.status"
                :items="[
                  { title: '전체', value: '' },
                  { title: '발송 성공', value: 'sent' },
                  { title: '발송 실패', value: 'failed' },
                  { title: '대기 중', value: 'pending' },
                ]"
                label="상태"
                hide-details
                density="compact"
                @update:model-value="refreshPage"
              />
            </VCol>

            <VCol
              cols="12"
              sm="4"
            >
              <VTextField
                v-model="filters.search"
                label="검색"
                placeholder="이메일, 제목, 내용 검색..."
                hide-details
                density="compact"
                prepend-icon="tabler-search"
                @update:model-value="refreshPage"
                @keyup.enter="refreshPage"
              />
            </VCol>
          </VRow>

          <!-- 로딩 상태 -->
          <VRow v-if="loading">
            <VCol
              cols="12"
              class="text-center py-8"
            >
              <VProgressCircular
                indeterminate
                color="primary"
              />
            </VCol>
          </VRow>

          <!-- 알림 로그 목록 -->
          <VRow v-else>
            <VCol cols="12">
              <VCard variant="outlined">
                <VList density="compact">
                  <VListItem
                    v-for="log in logs"
                    :key="log.id"
                    class="px-0"
                  >
                    <template #prepend>
                      <VIcon
                        :icon="getTypeIcon(log.type)"
                        :color="getTypeColor(log.type)"
                        class="me-2"
                      />
                    </template>

                    <VListItemTitle>
                      <div class="d-flex align-center">
                        <span class="text-body-1">{{ log.recipient || '알림' }}</span>
                      </div>
                      <VChip
                        v-if="log.template"
                        size="x-small"
                        color="grey-lighten-1"
                        class="ms-2"
                      >
                        {{ log.template.name }}
                      </VChip>
                    </VListItemTitle>

                    <VListItemSubtitle
                      v-if="log.subject"
                      class="text-body-2"
                    >
                      {{ log.subject }}
                    </VListItemSubtitle>

                    <VListItemSubtitle
                      v-else-if="log.content"
                      class="text-caption text-truncate"
                    >
                      {{ log.content }}
                    </VListItemSubtitle>

                    <template #append>
                      <div class="d-flex flex-column align-end">
                        <div class="d-flex align-center gap-2">
                          <VChip
                            :color="getStatusColor(log.status)"
                            size="small"
                          >
                            {{ log.status === 'sent' ? '성공' : log.status === 'failed' ? '실패' : '대기' }}
                          </VChip>

                          <VChip
                            v-if="log.type"
                            size="x-small"
                            variant="outlined"
                            class="text-grey-darken-1"
                          >
                            {{ log.type.toUpperCase() }}
                          </VChip>
                        </div>

                        <span class="text-caption text-grey">
                          {{ formatDate(log.created_at) }}
                        </span>
                      </div>
                    </template>
                  </VListItem>
                </VList>

                <!-- 데이터 없음 -->
                <div
                  v-if="logs.length === 0"
                  class="text-center py-8"
                >
                  <VIcon
                    icon="tabler-bell-x"
                    size="48"
                    color="grey"
                    class="mb-2"
                  />
                  <p class="text-grey-darken-1">
                    알림 이력이 없습니다.
                  </p>
                </div>
              </VCard>
            </VCol>
          </VRow>

          <!-- 페이지네이션 -->
          <VRow
            v-if="pagination.total > pagination.perPage"
            class="mt-4"
          >
            <VCol
              cols="12"
              class="d-flex justify-center"
            >
              <VPagination
                v-model:model-value="pagination.page"
                :length="pagination.total"
                :per-page="pagination.perPage"
                @update:model-value="fetchLogs"
              />
            </VCol>
          </VRow>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>

  <VSnackbar
    v-model="snackbar.isVisible.value"
    :color="snackbar.color.value"
    :timeout="snackbar.timeout.value"
  >
    {{ snackbar.message.value }}
  </VSnackbar>
</template>
